import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Target,
  Crosshair,
  MessageCircle,
  Users,
  ArrowRight,
  Lightbulb,
  Flag,
  Rocket,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  Diamond,
  Zap,
  Globe,
  Repeat,
  Sparkles,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";

// Custom hex to hsl conversion for color manipulation
const hexToHSL = (hex) => {
  // Remove the # if present
  hex = hex.replace(/^#/, "");

  // Parse the values
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255;
    g = parseInt(hex[1] + hex[1], 16) / 255;
    b = parseInt(hex[2] + hex[2], 16) / 255;
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  }

  // Find greatest and smallest values
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  // Calculate hue and saturation
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }

    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

// Convert HSL to HSL string
const hslToString = (hsl, alpha = 1) => {
  return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${alpha})`;
};

const StrategyCard = ({
  title,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
  color,
  questionTitles = {},
}) => {
  // Convert hex to HSL for better color manipulation
  const hsl = React.useMemo(() => {
    try {
      return hexToHSL(color || palette.primary);
    } catch (e) {
      return { h: 0, s: 0, l: 100 };
    }
  }, [color, palette]);

  // Create derived colors with different luminance
  const bgGradient = `linear-gradient(135deg, 
    ${hslToString({ ...hsl, l: 20 }, 0.1)} 0%, 
    ${hslToString({ ...hsl, l: 10 }, 0.05)} 100%)`;

  // Format text for better readability
  const formatText = (text) => {
    if (!text) return ["No information available"];

    // Convert to string if it's not already
    let textStr = text;
    if (typeof text !== "string") {
      if (typeof text === "object" && text !== null) {
        // If it's a scale object, extract the value
        if (typeof text.value !== "undefined") {
          textStr = String(text.value);
        } else if (text.description) {
          textStr = text.description;
        } else {
          try {
            textStr = JSON.stringify(text, null, 2);
          } catch (e) {
            textStr = "Complex object";
          }
        }
      } else {
        textStr = String(text);
      }
    }

    // If text is shorter than 200 chars, don't split it
    if (textStr.length < 200) return [textStr];

    // Split by sentences for better paragraph breaks
    const sentences = textStr.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 250) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Check for lists in the content (items separated by new lines, dashes, or numbers)
  const processListItems = (text) => {
    if (!text) return null;

    // Convert to string if it's not already
    let textStr = text;
    if (typeof text !== "string") {
      if (typeof text === "object" && text !== null) {
        // If it's a scale object, skip list processing
        if (typeof text.value !== "undefined") {
          return null;
        }
        if (text.description) {
          textStr = text.description;
        } else {
          try {
            textStr = JSON.stringify(text);
          } catch (e) {
            return null;
          }
        }
      } else {
        textStr = String(text);
      }
    }

    // Check if the text might be a list by looking for patterns
    const listPatterns = [
      /^\s*[-•*]\s+(.+)$/gm, // Bullet points
      /^\s*\d+\.\s+(.+)$/gm, // Numbered items
      /^\s*[a-z]\)\s+(.+)$/gm, // Letter items
    ];

    const hasListPattern = listPatterns.some((pattern) =>
      pattern.test(textStr)
    );

    if (hasListPattern) {
      // Split by new lines and process each line
      const lines = textStr.split(/\r?\n/).filter((line) => line.trim());

      return lines.map((line) => {
        // Clean up the line by removing bullet points, numbers, etc.
        return line
          .replace(/^\s*[-•*]\s+/, "")
          .replace(/^\s*\d+\.\s+/, "")
          .replace(/^\s*[a-z]\)\s+/, "")
          .trim();
      });
    }

    // Check if there are comma separated values that could be a list
    if (
      textStr.includes(",") &&
      textStr.split(",").length > 2 &&
      textStr.split(",").every((item) => item.trim().length < 40)
    ) {
      return textStr.split(",").map((item) => item.trim());
    }

    return null;
  };

  // Use ScaleBar for 1-10 scale values or scale objects
  const isScaleValue = (() => {
    if (
      typeof value === "object" &&
      value !== null &&
      typeof value.value === "number"
    )
      return true;
    let num = value;
    if (typeof value === "string") num = parseFloat(value);
    return typeof num === "number" && num >= 1 && num <= 10;
  })();

  // Try to detect if the value contains a list
  const listItems = processListItems(value);

  // If not a list, format as paragraphs
  const paragraphs = !listItems ? formatText(value) : null;

  // Helper to get left/right labels for scale
  const getScaleLabels = (title, value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      (value.leftLabel || value.rightLabel)
    ) {
      return {
        leftLabel: value.leftLabel || "",
        rightLabel: value.rightLabel || "",
      };
    }
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("formal") || lowerTitle.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerTitle.includes("playful") || lowerTitle.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerTitle.includes("modern") || lowerTitle.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerTitle.includes("loud") || lowerTitle.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerTitle.includes("bright") || lowerTitle.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return { leftLabel: "Low", rightLabel: "High" };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onClick={onToggle}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 cursor-pointer transition-all duration-300 ${
        isExpanded ? "col-span-full" : ""
      }`}
      style={{
        background: bgGradient,
        borderColor: isExpanded
          ? `hsla(${hsl.h}, ${hsl.s}%, 40%, 0.5)`
          : "rgba(255, 255, 255, 0.1)",
      }}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        boxShadow: `0 5px 15px rgba(0, 0, 0, 0.1), 0 0 10px ${hslToString(
          { ...hsl, l: 40 },
          0.2
        )}`,
        borderColor: `hsla(${hsl.h}, ${hsl.s}%, 40%, 0.5)`,
      }}
    >
      <motion.div
        className="p-6"
        initial={false}
        animate={{
          padding: isExpanded ? "1.5rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: `hsla(${hsl.h}, ${hsl.s}%, 30%, 0.9)` }}
              whileHover={{ scale: 1.1 }}
            >
              <Icon size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/60"
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>

        <AnimatePresence>
          {!isExpanded && (
            <motion.div
              className="text-white/70 line-clamp-3 text-sm"
              exit={{ opacity: 0, height: 0 }}
            >
              {isScaleValue
                ? (() => {
                    let num = value;
                    let leftLabel, rightLabel;
                    if (
                      typeof value === "object" &&
                      value !== null &&
                      typeof value.value === "number"
                    ) {
                      num = value.value;
                      leftLabel = value.leftLabel;
                      rightLabel = value.rightLabel;
                    } else {
                      if (typeof value === "string") num = parseFloat(value);
                      const labels = getScaleLabels(title, value);
                      leftLabel = labels.leftLabel;
                      rightLabel = labels.rightLabel;
                    }
                    const notes = typeof value === "object" && value !== null ? value.notes : null;
                    const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                    return (
                      <div>
                        <ScaleBar
                          value={num}
                          min={1}
                          max={10}
                          leftLabel={leftLabel}
                          rightLabel={rightLabel}
                          palette={palette}
                          label={title}
                          customTitle={questionTitles[title]}
                        />
                        {notes && notesVisible && (
                          <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                            <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                : listItems
                ? `${listItems.length} items identified`
                : (() => {
                    // Safely convert value to string for display
                    if (typeof value === "object" && value !== null) {
                      if (typeof value.value !== "undefined") {
                        return String(value.value);
                      }
                      if (value.description) {
                        return value.description;
                      }
                      try {
                        return JSON.stringify(value);
                      } catch (e) {
                        return "Complex object";
                      }
                    }
                    return value ? String(value) : "No information available";
                  })()}
            </motion.div>
          )}

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              {isScaleValue ? (
                (() => {
                  let num = value;
                  let leftLabel, rightLabel;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    typeof value.value === "number"
                  ) {
                    num = value.value;
                    leftLabel = value.leftLabel;
                    rightLabel = value.rightLabel;
                  } else {
                    if (typeof value === "string") num = parseFloat(value);
                    const labels = getScaleLabels(title, value);
                    leftLabel = labels.leftLabel;
                    rightLabel = labels.rightLabel;
                  }
                  const notes = typeof value === "object" && value !== null ? value.notes : null;
                  const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                  return (
                    <div>
                      <ScaleBar
                        value={num}
                        min={1}
                        max={10}
                        leftLabel={leftLabel}
                        rightLabel={rightLabel}
                        palette={palette}
                        label={title}
                      />
                      {notes && notesVisible && (
                        <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                          <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : listItems ? (
                <ul className="space-y-2 pl-2">
                  {listItems.map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start text-white/80"
                    >
                      <span
                        className="inline-block w-4 h-4 rounded-full mr-3 mt-1 flex-shrink-0"
                        style={{
                          backgroundColor: `hsla(${hsl.h}, ${hsl.s}%, 40%, 0.4)`,
                        }}
                      ></span>
                      <span>
                        {typeof item === "object" && item !== null
                          ? typeof item.value !== "undefined"
                            ? String(item.value)
                            : JSON.stringify(item)
                          : String(item)}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-4">
                  {paragraphs.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-white/80 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const DynamicBrandStrategySection = ({
  strategy,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 3;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );

        // Add color variants
        document.documentElement.style.setProperty(
          "--primary-color-20",
          `rgba(${hexToRgb(palette.primary)}, 0.2)`
        );
        document.documentElement.style.setProperty(
          "--primary-color-30",
          `rgba(${hexToRgb(palette.primary)}, 0.3)`
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Define icons for different strategy elements
  const icons = {
    Vision: Lightbulb,
    Mission: Flag,
    Goals: Target,
    Values: Rocket,
    "Target Audience": Users,
    Positioning: Crosshair,
    Messaging: MessageCircle,
    "Unique Selling Proposition": Diamond,
    "Brand Promise": Zap,
    "Value Proposition": Diamond,
    Competitors: Target,
    "Perception Change": Repeat,
    "Global Recognition": Globe,
    "Most On Brand": Sparkles,
    "Needs Improvement": TrendingUp,
    USP: Diamond,
  };

  // Create color variations based on primary color
  const primaryHsl = hexToHSL(palette?.primary || "#4a6cf7");
  const colorVariations = [
    { ...primaryHsl, h: (primaryHsl.h + 0) % 360 }, // Primary
    { ...primaryHsl, h: (primaryHsl.h + 30) % 360 }, // Analogous +30
    { ...primaryHsl, h: (primaryHsl.h + 330) % 360 }, // Analogous -30
    { ...primaryHsl, h: (primaryHsl.h + 60) % 360 }, // Tertiary +60
    { ...primaryHsl, h: (primaryHsl.h + 300) % 360 }, // Tertiary -60
    { ...primaryHsl, h: (primaryHsl.h + 180) % 360 }, // Complementary
  ];

  // Map HSL objects to hex strings for each color
  const getHexFromHsl = (hsl) => {
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
    const m = l - c / 2;

    let r, g, b;
    if (h < 1 / 6) {
      [r, g, b] = [c, x, 0];
    } else if (h < 2 / 6) {
      [r, g, b] = [x, c, 0];
    } else if (h < 3 / 6) {
      [r, g, b] = [0, c, x];
    } else if (h < 4 / 6) {
      [r, g, b] = [0, x, c];
    } else if (h < 5 / 6) {
      [r, g, b] = [x, 0, c];
    } else {
      [r, g, b] = [c, 0, x];
    }

    const toHex = (v) => {
      const hex = Math.round((v + m) * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const strategyColors = colorVariations.map(getHexFromHsl);

  // Map strategy data to cards with appropriate icons
  const getStrategyCards = () => {
    return Object.entries(strategy).map(([key, value], index) => {
      // Use displayTitle if available (for duplicate titles with question IDs)
      const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
        ? value.displayTitle 
        : key;
      
      // Find exact match first
      let Icon = icons[displayKey];

        // If no exact match, try to find partial match
        if (!Icon) {
          const lowerKey = displayKey.toLowerCase();
        for (const [iconKey, iconComponent] of Object.entries(icons)) {
          if (lowerKey.includes(iconKey.toLowerCase())) {
            Icon = iconComponent;
            break;
          }
        }
      }

      // Default to Target icon if no match found
      Icon = Icon || Target;

      return {
        title: displayKey, // Use display title for duplicate titles
        value: value,
        icon: Icon,
        color: strategyColors[index % strategyColors.length],
        index,
      };
    });
  };

  const strategyCards = getStrategyCards();

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < strategyCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(strategyCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              // Apply transparency mask for seamless blending
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Brand Strategy
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          The strategic approach and positioning that defines how the brand
          engages with its audience.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">
                Strategy Analysis
              </h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="brandStrategy"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Desktop View: Interactive Cards Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
            {strategyCards.map((card, index) => (
              <StrategyCard
                key={`strategy-${index}`}
                title={card.title}
                value={card.value}
                icon={card.icon}
                color={card.color}
                index={index}
                isExpanded={expandedCardIndex === index}
                onToggle={() => toggleCard(index)}
                palette={palette}
                questionTitles={questionTitles}
              />
            ))}
          </div>

          {/* Mobile View: Interactive Cards Carousel */}
          <div className="md:hidden relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <StrategyCard
                      key={index}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      color={card.color}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                      questionTitles={questionTitles}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicBrandStrategySection;
